import * as THREE from 'three'
import { RunConfig } from '../../config/runs'
import { getElevation } from '../../world/terrain/SlopeTerrain'

/**
 * Creates trees randomly placed ON the run that the skier must dodge.
 * These are collision obstacles — hitting one triggers a crash.
 */

function seededRandom(seed: number): number {
  const n = Math.sin(seed * 311.7) * 43758.5453
  return n - Math.floor(n)
}

function createObstacleTree(height: number): THREE.Group {
  const tree = new THREE.Group()

  const trunk = new THREE.Mesh(
    new THREE.CylinderGeometry(0.2, 0.25, height * 0.35, 6),
    new THREE.MeshStandardMaterial({ color: 0x5c3a1e, roughness: 0.8 })
  )
  trunk.position.y = height * 0.175
  trunk.castShadow = true
  tree.add(trunk)

  const foliageMat = new THREE.MeshStandardMaterial({ color: 0x1e4d1e, roughness: 0.7 })
  const layers = 3
  for (let i = 0; i < layers; i++) {
    const radius = (1.0 - i * 0.2) * (height / 3)
    const coneHeight = height * 0.35
    const cone = new THREE.Mesh(
      new THREE.ConeGeometry(radius, coneHeight, 7),
      foliageMat
    )
    cone.position.y = height * 0.3 + i * coneHeight * 0.5
    cone.castShadow = true
    tree.add(cone)
  }

  return tree
}

export interface ObstacleTreeData {
  mesh: THREE.Group
  position: THREE.Vector3
  radius: number  // collision radius
}

export function createObstacleTrees(config: RunConfig): ObstacleTreeData[] {
  if (!config.obstacleTrees) return []

  const obstacles: ObstacleTreeData[] = []
  // Scale tree density by difficulty — greens have few, double blacks have lots
  const densityMap: Record<string, number> = {
    green: 80,       // one tree every 80m
    blue: 50,        // one every 50m
    black: 25,       // one every 25m
    doubleBlack: 15, // one every 15m
  }
  const spacing = densityMap[config.difficulty] || 30
  const treeCount = Math.floor(config.length / spacing)

  for (let i = 0; i < treeCount; i++) {
    const seed = i * 17.3 + 99
    // Keep within the run boundaries but leave edges clear
    const x = (seededRandom(seed) - 0.5) * (config.width * 0.75)
    const z = -config.length / 2 + 50 + (i / treeCount) * (config.length * 0.8)
    // Stagger so they're not in a grid
    const zOffset = (seededRandom(seed + 3) - 0.5) * 15

    const height = 3 + seededRandom(seed + 1) * 2
    const groundY = getElevation(x, z + zOffset)

    const tree = createObstacleTree(height)
    tree.position.set(x, groundY, z + zOffset)

    obstacles.push({
      mesh: tree,
      position: new THREE.Vector3(x, groundY, z + zOffset),
      radius: 0.8, // collision radius
    })
  }

  return obstacles
}
